import { useState } from 'react';

import { Platform } from '../../platform';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import type { AvatarProps } from './Avatar.types';
import { avatarInitialsVariants, avatarVariants } from './Avatar.variants';
import { AVATAR_TEXT_COLOR, avatarBgColorClass } from './avatarColors';
import { AvatarImage } from './AvatarImage';
import { initialsFor } from './initials';
import { isPlaceholderAvatarSrc } from './isPlaceholderAvatarSrc';

const IS_WEB = Platform.OS === 'web';

// Native-only text-style fix for vertical centring of the initials — see the
// usage site. Module-scoped so it isn't rebuilt on every render. Harmless on
// web (unknown style keys are ignored).
const INITIALS_CENTER_FIX = {
  style: { includeFontPadding: false, textAlignVertical: 'center' as const },
};

/**
 * Cross-platform Avatar — a circular user picture that falls back to an
 * initials chip when there's no image (or it fails to load). The chip's
 * background is a deterministic per-name colour from the deck palette
 * (see avatarColors). Renders identically on web and React Native.
 *
 * @example
 * ```tsx
 * import { Avatar } from '@flashka/shared/ui';
 *
 * // Photo (falls back to "DB" automatically if the URL 404s)
 * <Avatar src={user.imageUrl} name="Diana Bell" size="l" />
 *
 * // Initials fallback
 * <Avatar name="Diana Bell" size="2xs" />
 * ```
 */
export function Avatar({
  size,
  src,
  name,
  className,
  textClassName,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
}: AvatarProps) {
  // Remember which src failed so a NEW src gets a fresh attempt without
  // needing an effect to reset the flag.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  // A provider placeholder (legacy Clerk default silhouette) counts as
  // "no image" so it falls back to initials — see isPlaceholderAvatarSrc.
  const showImage = !!src && src !== failedSrc && !isPlaceholderAvatarSrc(src);

  return (
    <View
      className={cn(
        avatarVariants({ size, variant: showImage ? 'image' : 'initials' }),
        // Per-name background from the deck palette (initials mode only).
        !showImage && avatarBgColorClass(name),
        className,
      )}
      // Web ARIA and RN name the image role differently.
      accessibilityRole={IS_WEB ? 'img' : 'image'}
      accessibilityLabel={accessibilityLabel ?? name}
      testID={testID}
      webProps={webProps}
      nativeProps={nativeProps}>
      {showImage ? (
        <AvatarImage
          src={src}
          className="h-full w-full"
          onError={() => setFailedSrc(src)}
        />
      ) : (
        <Text
          className={cn(
            avatarInitialsVariants({ size }),
            AVATAR_TEXT_COLOR,
            textClassName,
          )}
          // Android reserves extra line padding that pushes single-line text
          // toward the top of a centred box (visible on the large profile
          // hero — the initials sat high). `includeFontPadding: false` +
          // `textAlignVertical: 'center'` remove it; both are no-ops on web/iOS.
          nativeProps={INITIALS_CENTER_FIX}
          selectable={false}>
          {initialsFor(name)}
        </Text>
      )}
    </View>
  );
}
(Avatar as { displayName?: string }).displayName = 'Avatar';
